/**
 * Card-related runtime utilities, constants, and helper functions.
 *
 * This module contains runtime code (constants, helpers) extracted from types/card.ts
 * and consolidates duplicated helpers from various components.
 * For type definitions, see types/card.ts.
 * For user settings constants, see lib/settingsUtils.ts.
 */
import type { DropdownOption } from '@/components/Dropdown/Dropdown';
import type {
  CardMainType,
  CardSuperType,
  Color,
  Condition,
  Finish,
  ImageUris,
  ImportSource,
  Language,
  Rarity,
  ScryfallCard,
  TypeCategory,
} from '@/types/card';

// ============================================================================
// Multi-Face Card Utilities
// ============================================================================

/** Resolves the primary display image for any card layout (face 0 for DFCs, card-level otherwise) */
export function getDisplayImageUris(card: ScryfallCard | null | undefined): ImageUris | null {
  if (!card) {
    console.warn('getDisplayImageUris called with null card');
    return null;
  }
  return card.card_faces?.[0]?.image_uris ?? card.image_uris;
}

/** Aggregates oracle text from all faces for cards where card-level text is null */
export function getFullOracleText(card: ScryfallCard | null | undefined): string | null {
  if (!card) {
    console.warn('getFullOracleText called with null card');
    return null;
  }
  if (card.oracle_text) return card.oracle_text;
  if (!card.card_faces) return null;
  const texts = card.card_faces.map((f) => f.oracle_text).filter(Boolean);
  return texts.length > 0 ? texts.join(' // ') : null;
}

/** Aggregates type_line from card faces when the card-level type_line is missing */
export function getFullTypeLine(card: ScryfallCard | null | undefined): string | null {
  if (!card) {
    console.warn('getFullTypeLine called with null card');
    return null;
  }
  if (card.type_line) return card.type_line;
  if (!card.card_faces) return '';
  const types = card.card_faces.map((f) => f.type_line).filter(Boolean);
  return types.length > 0 ? types.join(' // ') : '';
}

/**
 * Resolves oracle_id, falling back to face[0] for reversible_card layout.
 * Returns null if neither the card nor its faces provide an oracle_id.
 * Callers using this as a Map key or cache key must guard against null.
 */
export function getOracleId(card: ScryfallCard | null | undefined): string | null {
  if (!card) {
    console.warn('getOracleId called with null card');
    return null;
  }
  return card.oracle_id ?? card.card_faces?.[0]?.oracle_id ?? null;
}

/** Resolves type_line from face[0] for reversible_card layout. For sorting/display (single face). */
export function getTypeLine(card: ScryfallCard | null | undefined): string | null {
  if (!card) {
    console.warn('getTypeLine called with null card');
    return null;
  }
  return card.type_line ?? card.card_faces?.[0]?.type_line ?? '';
}

/** Resolves cmc from face[0] for reversible_card layout. */
/** TODO: re-evaluate for other layouts */
export function getCmc(card: ScryfallCard | null | undefined): number | null {
  if (!card) {
    console.warn('getCmc called with null card');
    return null;
  }
  return card.cmc ?? card.card_faces?.[0]?.cmc ?? 0;
}

/** Resolves mana_cost from face[0] for reversible_card layout. */
/** TODO: re-evaluate for other layouts */
export function getManaCost(card: ScryfallCard | null | undefined): string | null {
  if (!card) {
    console.warn('getManaCost called with null card');
    return null;
  }
  return card.mana_cost ?? card.card_faces?.[0]?.mana_cost ?? null;
}

/** Resolves colors from face[0] for reversible_card layout. */
/** TODO: re-evaluate for other layouts */
export function getColors(card: ScryfallCard | null | undefined): Color[] | null {
  if (!card) {
    console.warn('getColors called with null card');
    return null;
  }
  return card.colors ?? card.card_faces?.[0]?.colors ?? [];
}

/**
 * Display mana cost for any layout: the card-level cost when present (single-faced,
 * split, adventure, flip), otherwise the distinct non-empty face costs joined with " // ".
 * Use this for rendering and pip counting; use `getManaCost` when logic needs a single face.
 */
export function getDisplayManaCost(card: ScryfallCard | null | undefined): string | null {
  if (!card) {
    console.warn('getDisplayManaCost called with null card');
    return null;
  }
  if (card.mana_cost) return card.mana_cost;
  if (card.card_faces) {
    // Dedupe so reversible_card (identical faces) never renders "{X} // {X}"
    const costs = [...new Set(card.card_faces.map((face) => face.mana_cost).filter((cost): cost is string => Boolean(cost)))];
    if (costs.length > 0) return costs.join(' // ');
  }
  return card.mana_cost ?? null;
}

/** Resolves power from face[0] when the card-level value is null (all DFC layouts). */
export function getPower(card: ScryfallCard | null | undefined): string | null {
  if (!card) {
    console.warn('getPower called with null card');
    return null;
  }
  return card.power ?? card.card_faces?.[0]?.power ?? null;
}

/** Resolves toughness from face[0] when the card-level value is null (all DFC layouts). */
export function getToughness(card: ScryfallCard | null | undefined): string | null {
  if (!card) {
    console.warn('getToughness called with null card');
    return null;
  }
  return card.toughness ?? card.card_faces?.[0]?.toughness ?? null;
}

// ============================================================================
// Display Labels
// ============================================================================

export const CONDITION_LABELS: Record<Condition, string> = {
  M: 'Mint',
  NM: 'Near Mint',
  LP: 'Lightly Played',
  MP: 'Moderately Played',
  HP: 'Heavily Played',
  D: 'Damaged',
};

export const FINISH_LABELS: Record<Finish, string> = {
  nonfoil: 'Non-Foil',
  foil: 'Foil',
  etched: 'Etched',
};

/**
 * Finishes this printing was produced in, from Scryfall's `finishes` field.
 * Returns null when unknown (card docs synced before the field was stored) — callers should allow every finish.
 */
export function getPrintingFinishes(card: Pick<ScryfallCard, 'finishes'> | null | undefined): Finish[] | null {
  const known = (card?.finishes ?? []).filter((finish): finish is Finish => finish in FINISH_LABELS);
  return known.length > 0 ? known : null;
}

export const LANGUAGE_LABELS: Record<Language, string> = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  pt: 'Portuguese',
  ja: 'Japanese',
  ko: 'Korean',
  ru: 'Russian',
  zhs: 'Simplified Chinese',
  zht: 'Traditional Chinese',
  ph: 'Phyrexian',
};

export const CONDITION_OPTIONS: DropdownOption<Condition>[] = (Object.entries(CONDITION_LABELS) as [Condition, string][]).map(
  ([value, label]) => ({ value, label }),
);

export const LANGUAGE_OPTIONS: DropdownOption<Language>[] = (Object.entries(LANGUAGE_LABELS) as [Language, string][]).map(
  ([value, label]) => ({ value, label }),
);

export const RARITY_LABELS: Record<Rarity, string> = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  mythic: 'Mythic Rare',
  special: 'Special',
  bonus: 'Bonus',
};

/** Tailwind text-color class for a card name colored by its rarity. */
export function getRarityTextClass(rarity: Rarity | undefined): string {
  switch (rarity) {
    case 'mythic':
      return 'text-r-mythic!';
    case 'rare':
      return 'text-r-rare!';
    case 'uncommon':
      return 'text-r-uncommon!';
    case 'common':
      return 'text-r-common!';
    case 'special':
    case 'bonus':
      return 'text-r-other!';
    default:
      return 'text-zinc-300';
  }
}

export const SOURCE_LABELS: Record<ImportSource, string> = {
  manual: 'Manual Entry',
  unknown: 'Unknown',
  dragonshield: 'Dragon Shield',
  delverlens: 'DelverLens',
  'delverlens-legacy': 'DelverLens (Legacy)',
  moxfield: 'Moxfield',
  archidekt: 'Archidekt',
  manabox: 'ManaBox',
};

// ============================================================================
// Set Type Labels
// ============================================================================

export const SET_TYPE_LABELS: Record<string, string> = {
  core: 'Core Set',
  expansion: 'Expansion',
  masters: 'Masters',
  masterpiece: 'Masterpiece',
  arsenal: 'Arsenal',
  from_the_vault: 'From the Vault',
  spellbook: 'Spellbook',
  premium_deck: 'Premium Deck',
  duel_deck: 'Duel Deck',
  draft_innovation: 'Draft Innovation',
  treasure_chest: 'Treasure Chest',
  commander: 'Commander',
  planechase: 'Planechase',
  archenemy: 'Archenemy',
  vanguard: 'Vanguard',
  funny: 'Funny',
  starter: 'Starter',
  box: 'Box Set',
  promo: 'Promo',
  token: 'Token',
  memorabilia: 'Memorabilia',
  alchemy: 'Alchemy',
  minigame: 'Minigame',
};

// ============================================================================
// ============================================================================
// Type Line Parsing — Canonical Functions
// ============================================================================

/** All recognized MTG super types (lowercase) */
export const CARD_SUPER_TYPES: readonly CardSuperType[] = ['basic', 'legendary', 'ongoing', 'snow', 'world'] as const;

/** All recognized MTG main types (lowercase) */
export const CARD_MAIN_TYPES: readonly CardMainType[] = [
  'artifact',
  'battle',
  'conspiracy',
  'creature',
  'dungeon',
  'emblem',
  'enchantment',
  'instant',
  'kindred',
  'land',
  'phenomenon',
  'plane',
  'planeswalker',
  'scheme',
  'sorcery',
  'token',
  'vanguard',
] as const;

/** O(1) lookup sets for type classification */
const SUPER_TYPES_SET: ReadonlySet<string> = new Set(CARD_SUPER_TYPES);
const MAIN_TYPES_SET: ReadonlySet<string> = new Set(CARD_MAIN_TYPES);

/**
 * Priority order for primary type classification.
 * The first matching type wins. Land is 2nd so "Artifact Land" → 'land'.
 */
const TYPE_CATEGORY_PRIORITY: readonly CardMainType[] = [
  'creature',
  'land',
  'planeswalker',
  'battle',
  'instant',
  'sorcery',
  'artifact',
  'enchantment',
  'kindred',
  'conspiracy',
  'dungeon',
  'phenomenon',
  'plane',
  'scheme',
  'vanguard',
  'token',
  'emblem',
] as const;

/** Capitalized display labels for type categories */
export const TYPE_CATEGORY_LABELS: Record<TypeCategory, string> = {
  artifact: 'Artifact',
  battle: 'Battle',
  conspiracy: 'Conspiracy',
  creature: 'Creature',
  dungeon: 'Dungeon',
  emblem: 'Emblem',
  enchantment: 'Enchantment',
  instant: 'Instant',
  kindred: 'Kindred',
  land: 'Land',
  phenomenon: 'Phenomenon',
  plane: 'Plane',
  planeswalker: 'Planeswalker',
  scheme: 'Scheme',
  sorcery: 'Sorcery',
  token: 'Token',
  vanguard: 'Vanguard',
  other: 'Other',
};

/**
 * Extract supertypes from a type line (first face only for multi-faced cards).
 * "Legendary Snow Creature — Human Wizard" → ['legendary', 'snow']
 */
export function getSuperTypes(typeLine: string): CardSuperType[] {
  if (!typeLine) return [];
  const firstFace = typeLine.split('//')[0];
  const mainPart = firstFace.split('—')[0].trim().toLowerCase();
  const words = mainPart.split(/\s+/);
  return words.filter((w): w is CardSuperType => SUPER_TYPES_SET.has(w));
}

/**
 * Extract main types from a type line (first face only for multi-faced cards).
 * "Artifact Creature — Construct" → ['artifact', 'creature']
 * "Basic Land — Plains" → ['land']
 */
export function getTypes(typeLine: string): CardMainType[] {
  if (!typeLine) return [];
  const firstFace = typeLine.split('//')[0];
  const mainPart = firstFace.split('—')[0].trim().toLowerCase();
  const words = mainPart.split(/\s+/);
  return words.filter((w): w is CardMainType => MAIN_TYPES_SET.has(w));
}

/**
 * Extract subtypes from a type line (first face only for multi-faced cards).
 * "Legendary Creature — Human Soldier" → ['Human', 'Soldier']
 * "Artifact — Equipment" → ['Equipment']
 * "Enchantment" → []
 */
export function getSubTypes(typeLine: string): string[] {
  if (!typeLine) return [];
  const firstFace = typeLine.split('//')[0];
  const parts = firstFace.split('—');
  if (parts.length < 2) return [];
  return parts[1].trim().split(/\s+/).filter(Boolean);
}

/**
 * Format power/toughness for display
 * Returns "P/T" format for creatures, null for other card types
 */
export function formatPowerToughness(card: { power?: string | null; toughness?: string | null; type_line: string | null }): string | null {
  const typeLine = (card.type_line ?? '').toLowerCase();

  // Creatures: show P/T
  if (typeLine.includes('creature') && card.power && card.toughness) {
    return `${card.power}/${card.toughness}`;
  }

  // Planeswalkers: loyalty is typically in card data but we don't have it in MinimalCard
  // For now, return null for non-creatures
  return null;
}

/** Formats the combat/loyalty/defense stat of a single card face. */
function formatFaceStats(face: {
  power?: string | null;
  toughness?: string | null;
  loyalty?: string | null;
  defense?: string | null;
}): string | null {
  if (face.power != null && face.toughness != null) return `${face.power}/${face.toughness}`;
  if (face.loyalty != null) return `[${face.loyalty}]`;
  if (face.defense != null) return `<${face.defense}>`;
  return null;
}

/**
 * Format power/toughness, planeswalker loyalty (`[4]`) or battle defense (`<5>`)
 * for any layout. Multi-face cards join their faces with " // ", using "-" for
 * faces with no stat. Returns null when no face has a stat.
 */
export function formatPowerToughnessFull(card: ScryfallCard | null | undefined): string | null {
  if (!card) {
    console.warn('formatPowerToughnessFull called with null card');
    return null;
  }

  const cardLevel = formatFaceStats(card);
  if (cardLevel) return cardLevel;

  if (!card.card_faces) return null;

  const faceStats = card.card_faces.map(formatFaceStats);
  if (faceStats.every((stat) => stat === null)) return null;

  return faceStats.map((stat) => stat ?? '-').join(' // ');
}

// ============================================================================
// Card Type Classification
// ============================================================================

/**
 * Determine the single primary card type for grouping/sorting.
 * Uses first face only for multi-faced cards (split by "//").
 * Priority: creature > land > planeswalker > battle > instant > sorcery > artifact > enchantment > ...
 * An "Artifact Land" classifies as 'land'.
 */
export function getTypeCategory(typeLine: string): TypeCategory {
  if (!typeLine) return 'other';
  const firstFace = typeLine.split('//')[0];
  const mainPart = firstFace.split('—')[0].trim().toLowerCase();
  for (const type of TYPE_CATEGORY_PRIORITY) {
    if (mainPart.includes(type)) return type;
  }
  return 'other';
}

/**
 * Check if a card is a land (for curve exclusion, statistics, etc.)
 */
export function isLand(typeLine: string): boolean {
  if (!typeLine) return false;
  return typeLine.toLowerCase().includes('land');
}

// ============================================================================
// Mana Pip Counting (from useDeckStatistics.ts)
// ============================================================================

/** Regex to match mana pips like {W}, {U}, {B}, {R}, {G}, {C} */
const MANA_PIP_REGEX = /\{([WUBRGC])\}/gi;

/**
 * Parse mana cost string and count pips per color
 * Examples: "{2}{W}{W}" -> { W: 2 }, "{1}{U}{B}" -> { U: 1, B: 1 }
 */
export function countManaPips(manaCost: string | null): Record<Color | 'C', number> {
  const pips: Record<Color | 'C', number> = { W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 };

  if (!manaCost) return pips;

  let match;
  while ((match = MANA_PIP_REGEX.exec(manaCost)) !== null) {
    const color = match[1].toUpperCase() as Color | 'C';
    if (color in pips) {
      pips[color]++;
    }
  }

  return pips;
}

// ============================================================================
// Mana Source Detection (from useManaBaseAnalysis.ts)
// ============================================================================

/** Keywords indicating mana dorks */
const MANA_DORK_KEYWORDS = ['mana dork'];

/** Oracle text patterns for mana production */
const MANA_PRODUCTION_PATTERNS = [
  /add \{[wubrgc]\}/i,
  /add one mana of any color/i,
  /add one mana of any type/i,
  /add one mana of that color/i,
  /add one mana of the chosen color/i,
  /adds? \{[wubrgc]\}.*mana/i,
];

/** Oracle text patterns for mana rocks */
const MANA_ROCK_PATTERNS = [/\{t\}.*add \{/i, /\{t\}:.*add.*mana/i];

/** Basic land names (lowercase for matching) */
const BASIC_LAND_NAMES_LOWER = ['plains', 'island', 'swamp', 'mountain', 'forest', 'wastes'];

/** Land types to color mapping */
const LAND_TYPE_TO_COLOR: Record<string, Color> = {
  plains: 'W',
  island: 'U',
  swamp: 'B',
  mountain: 'R',
  forest: 'G',
};

/**
 * Check if a card is a basic land (by type line and name)
 * More robust than name-only checks - requires "Basic Land" in type line
 */
export function isBasicLandCard(typeLine: string, name: string): boolean {
  const lowerType = typeLine.toLowerCase();
  const lowerName = name.toLowerCase();
  return lowerType.includes('basic') && lowerType.includes('land') && BASIC_LAND_NAMES_LOWER.some((b) => lowerName.includes(b));
}

/**
 * Check if a land is a utility land (has non-mana activated abilities)
 */
export function isUtilityLand(typeLine: string, oracleText: string | null): boolean {
  if (!typeLine.toLowerCase().includes('land')) return false;
  if (!oracleText) return false;

  // Utility lands typically have activated abilities that aren't just mana production
  // Look for abilities that sacrifice, draw, deal damage, etc.
  const utilityPatterns = [/sacrifice.*:(?!.*add)/i, /deal \d+ damage/i, /draw a card/i, /create.*token/i, /destroy/i, /exile/i];

  return utilityPatterns.some((p) => p.test(oracleText));
}

/**
 * Check if a card is a mana dork (creature that produces mana)
 */
export function isManaDork(typeLine: string, oracleText: string | null, keywords: string[]): boolean {
  if (!typeLine.toLowerCase().includes('creature')) return false;

  // Check keywords
  if (keywords.some((k) => MANA_DORK_KEYWORDS.includes(k.toLowerCase()))) {
    return true;
  }

  // Check oracle text for mana production
  if (!oracleText) return false;
  return MANA_PRODUCTION_PATTERNS.some((p) => p.test(oracleText));
}

/**
 * Check if a card is a mana rock (artifact that produces mana)
 */
export function isManaRock(typeLine: string, oracleText: string | null): boolean {
  const lowerType = typeLine.toLowerCase();
  if (!lowerType.includes('artifact')) return false;
  if (lowerType.includes('creature')) return false; // Artifact creatures are dorks

  if (!oracleText) return false;
  return MANA_ROCK_PATTERNS.some((p) => p.test(oracleText)) || MANA_PRODUCTION_PATTERNS.some((p) => p.test(oracleText));
}

// ============================================================================
// Mana Rock Keepability Scoring
// ============================================================================

/** Regex to count mana symbols after "Add" in oracle text (e.g., "{T}: Add {C}{C}.") */
const MANA_SYMBOL_COUNT_PATTERN = /\{t\}[^.]*?add\s+((?:\{[wubrgc]\}\s*)+)/gi;

/** Pattern for "add X mana" style text */
const ADD_N_MANA_PATTERN = /add (\w+) mana/i;

const WORD_TO_NUMBER: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
};

/**
 * Parse oracle text to determine how many mana a card produces per single tap activation.
 * Counts mana symbols in "{T}: Add {X}{X}..." patterns.
 * Returns 1 as default for generic mana production patterns.
 * Returns 0 if no mana production detected.
 */
export function getManaProducedCount(oracleText: string | null): number {
  if (!oracleText) return 0;

  // Try to match "{T}: Add {C}{C}" style — count the symbols
  const symbolMatches = [...oracleText.matchAll(MANA_SYMBOL_COUNT_PATTERN)];
  if (symbolMatches.length > 0) {
    // Take the first tap-for-mana ability, count symbols
    const symbolGroup = symbolMatches[0][1];
    const count = (symbolGroup.match(/\{[wubrgc]\}/gi) || []).length;
    if (count > 0) return count;
  }

  // Try "add X mana" pattern (e.g., "add three mana")
  const wordMatch = oracleText.match(ADD_N_MANA_PATTERN);
  if (wordMatch) {
    const num = WORD_TO_NUMBER[wordMatch[1].toLowerCase()];
    if (num) return num;
    const parsed = parseInt(wordMatch[1], 10);
    if (!isNaN(parsed)) return parsed;
  }

  // Fallback: if it matches any mana production pattern at all, assume 1
  if (MANA_PRODUCTION_PATTERNS.some((p) => p.test(oracleText))) return 1;

  return 0;
}

/**
 * Calculate the land-equivalent value of a mana rock in an opening hand.
 *
 * Gate: returns 0 if cmc > landsInHand (can't cast it).
 * Formula: min(1.0, producedMana^0.6 / (1 + cmc))
 *
 * Examples:
 * - Sol Ring (cmc=1, produced=2, 1 land) → 0.76
 * - Arcane Signet (cmc=2, produced=1, 2 lands) → 0.33
 * - 3-CMC rock (cmc=3, produced=1, 3 lands) → 0.25
 * - Mana Crypt (cmc=0, produced=2, 0 lands) → 1.0 (capped)
 */
export function rockLandEquivalent(cmc: number, producedMana: number, landsInHand: number): number {
  if (cmc > landsInHand) return 0;
  if (producedMana <= 0) return 0;
  return Math.min(1.0, producedMana ** 0.6 / (1 + cmc));
}

/**
 * Get colors produced by a mana source (land, dork, or rock).
 *
 * Uses `producedMana` (Scryfall field) when available; falls back to oracle text
 * regex parsing. Pass optional `typeLine` to activate basic land type matching
 * via LAND_TYPE_TO_COLOR (for lands only).
 *
 * Fetch lands are detected via oracle text ("search your library for a ... card")
 * and count as sources for the colors they can find. Generic fetches ("any basic land")
 * use the optional `deckColors` parameter to count only toward colors in the deck.
 */
export function getManaSourceColors(
  oracleText: string | null,
  producedMana: Color[] | null,
  typeLine?: string,
  deckColors?: Color[],
): Color[] {
  // Use produced_mana field when available (more accurate than regex parsing)
  if (producedMana && producedMana.length > 0) {
    return producedMana.filter((c) => ['W', 'U', 'B', 'R', 'G'].includes(c));
  }

  const colors: Set<Color> = new Set();

  // When typeLine is provided, check basic land types (dual lands, fetch targets, etc.)
  if (typeLine) {
    const lowerType = typeLine.toLowerCase();
    for (const [landType, color] of Object.entries(LAND_TYPE_TO_COLOR)) {
      if (lowerType.includes(landType)) {
        colors.add(color);
      }
    }
  }

  if (!oracleText) return Array.from(colors);

  // Check for specific color production: "add {W}", "add {U}", etc.
  const addManaMatch = oracleText.match(/add \{([wubrgc])\}/gi);
  if (addManaMatch) {
    for (const match of addManaMatch) {
      const colorMatch = match.match(/\{([wubrgc])\}/i);
      if (colorMatch) {
        const color = colorMatch[1].toUpperCase();
        if (color !== 'C' && ['W', 'U', 'B', 'R', 'G'].includes(color)) {
          colors.add(color as Color);
        }
      }
    }
  }

  // "any color", "any type", or "choose a color" sources produce all five colors
  if (
    /add.*mana of any color/i.test(oracleText) ||
    /any type/i.test(oracleText) ||
    /add one mana of that color/i.test(oracleText) ||
    /add one mana of the chosen color/i.test(oracleText)
  ) {
    (['W', 'U', 'B', 'R', 'G'] as Color[]).forEach((c) => colors.add(c));
  }

  // Fetch land detection: "search your library for a(n) X or Y card"
  // Only apply if we haven't already found colors via other methods
  if (colors.size === 0) {
    const fetchColors = getFetchLandColors(oracleText, deckColors);
    for (const c of fetchColors) {
      colors.add(c);
    }
  }

  return Array.from(colors);
}

/**
 * Detect colors a fetch land can produce by parsing oracle text.
 * Handles typed fetches ("Island or Swamp") and generic fetches ("basic land card").
 */
function getFetchLandColors(oracleText: string, deckColors?: Color[]): Color[] {
  // Generic "any basic land" fetches (Evolving Wilds, Terramorphic Expanse, Prismatic Vista)
  if (/search your library for a basic land card/i.test(oracleText)) {
    // Count toward deck's colors only (or all 5 if no deck context)
    return deckColors && deckColors.length > 0 ? [...deckColors] : (['W', 'U', 'B', 'R', 'G'] as Color[]);
  }

  // Typed fetches: "search your library for an Island or Swamp card"
  // Matches patterns like: "search your library for a Plains, Island, Swamp, Mountain, or Forest card"
  const typedFetchMatch = oracleText.match(/search your library for an? (.+?) card/i);
  if (typedFetchMatch) {
    const landTypesText = typedFetchMatch[1].toLowerCase();
    const colors: Color[] = [];
    for (const [landType, color] of Object.entries(LAND_TYPE_TO_COLOR)) {
      if (landTypesText.includes(landType)) {
        colors.push(color);
      }
    }
    if (colors.length > 0) return colors;
  }

  return [];
}

/**
 * Convert color abbreviation to full name
 */
export function getColorName(color: Color): string {
  const names: Record<Color, string> = {
    W: 'white',
    U: 'blue',
    B: 'black',
    R: 'red',
    G: 'green',
  };
  return names[color];
}
